import React, { useMemo, useState } from "react";
import { FaTrash, FaCartPlus, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import { useUser } from "../../Home/UserContext";
import { createReglement } from "../../../services/achatsServices";
import { updateArticle } from "../../../services/articlesServices";
import {
  sendNotification,
  sendNotificationToAdmin,
} from "../../../services/notificationServices";
import { toAbsoluteArticlePhotoUrl } from "../../../services/articlesPhotoServices";

export default function CartModal({
  cart,
  setCart,
  closeModal,
  removeFromCart,
  onOrderSuccess,
}) {
  const FALLBACK = "/img/no-image.jpg";
  const { user } = useUser();
  const [ordering, setOrdering] = useState(false);

  const totalPrice = useMemo(
    () =>
      cart.reduce(
        (acc, it) => acc + Number(it.prix || 0) * Number(it.quantity || 0),
        0
      ),
    [cart]
  );

  const handleQuantityChange = (id, inc) => {
    setCart((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const next = Math.max(1, (it.quantity || 1) + inc);
        if (it.is_stockable && next > (it.quantite_stock ?? 0)) {
          toast.warn(`Stock insuffisant pour "${it.nom}"`);
          return it;
        }
        return { ...it, quantity: next };
      })
    );
  };

  const handleMessageChange = (e, idx) => {
    const msg = e.target.value;
    setCart((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, message: msg } : it))
    );
  };

  const handleOrder = async () => {
    if (!cart.length) {
      toast.warn("Votre panier est vide.");
      return;
    }

    const local = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const user_achat_id = local?.userId || user?.id || user?.userId;
    const token =
      local?.token ||
      user?.token ||
      user?.accessToken ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null;

    if (!user_achat_id) {
      toast.error("Session utilisateur invalide. Veuillez vous reconnecter.");
      return;
    }

    const date_achat = new Date().toISOString();

    try {
      setOrdering(true);

      const orderDetails = [];
      for (const item of cart) {
        if (item.is_stockable && (item.quantite_stock ?? 0) < item.quantity) {
          toast.error(`Stock insuffisant pour "${item.nom}" !`);
          setOrdering(false);
          return;
        }

        const reglement = {
          user_achat_id,
          article_id: item.id,
          quantite: item.quantity,
          prix_total: Number(item.prix || 0) * Number(item.quantity || 0),
          date_achat,
          message_achat: item.message || "",
          statut_reglement: "Non Réglé",
          type_reglement: "En ligne",
        };
        await createReglement(reglement);
        orderDetails.push(
          `${item.quantity}× ${item.nom} (${Number(item.prix).toFixed(2)}€)`
        );

        if (item.is_stockable) {
          const newStock = Math.max(
            0,
            Number(item.quantite_stock || 0) - Number(item.quantity || 0)
          );
          await updateArticle(item.id, { quantite_stock: newStock });
        }
      }

      if (token) {
        const userMsg = `Commande enregistrée : ${orderDetails.join(", ")}.`;
        try {
          await sendNotification(userMsg, user_achat_id, token);
        } catch (e) {
          console.error("Notification utilisateur échouée:", e);
        }

        const adminMsg = `${(local?.nom || user?.nom || "").trim()} ${(
          local?.prenom ||
          user?.prenom ||
          ""
        ).trim()} a commandé : ${orderDetails.join(", ")}.`;
        try {
          // Un seul appel côté serveur qui diffuse à tous les admins
          await sendNotificationToAdmin(adminMsg, token);
        } catch (e) {
          console.error("Notification admin échouée:", e);
        }
      }

      toast.success("Commande passée avec succès !");
      setCart([]);
      closeModal();
      onOrderSuccess?.();
    } catch (e) {
      console.error("Erreur lors de la commande:", e);
      toast.error("Erreur lors de la commande.");
    } finally {
      setOrdering(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm p-3 sm:p-4 grid place-items-center"
      onClick={(e) => e.currentTarget === e.target && closeModal()}
      role="dialog"
      aria-modal="true"
      aria-label="Panier"
    >
      <div
        className="w-full max-w-4xl h-[92vh] max-h-[92vh] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 px-4 py-3 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold">Votre Panier</h3>
          <button
            type="button"
            onClick={closeModal}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5 space-y-4 pb-24">
          {!cart.length && (
            <div className="text-center py-12 text-slate-600 dark:text-slate-300">
              Votre panier est vide.
            </div>
          )}

          {cart.map((item, index) => {
            const imageSrc = item.photo_url
              ? toAbsoluteArticlePhotoUrl(item.photo_url)
              : FALLBACK;

            return (
              <div
                key={item.id}
                className="rounded-xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-800/70 bg-white dark:bg-slate-900 shadow"
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Image : taille contrôlée en mobile */}
                  <div className="w-full sm:w-40 bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
                    <img
                      src={imageSrc}
                      alt={item.nom}
                      className="w-full sm:w-40 h-40 sm:h-full object-cover"
                      style={{ aspectRatio: "3 / 4" }}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = FALLBACK;
                      }}
                    />
                  </div>

                  {/* Infos */}
                  <div className="flex-1 p-4 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="text-slate-900 dark:text-slate-100 font-semibold">
                        {item.nom}
                      </h4>
                      <button
                        type="button"
                        className="text-rose-600 hover:text-rose-700"
                        onClick={() => removeFromCart(item.id)}
                        title="Retirer"
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      {item.description || (
                        <em className="text-slate-500">Pas de description.</em>
                      )}
                    </p>

                    <div className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                      {item.taille && (
                        <p>
                          Taille :{" "}
                          <span className="font-medium">{item.taille}</span>
                        </p>
                      )}
                      {item.is_stockable && (
                        <p>
                          Stock :{" "}
                          <span
                            className={
                              Number(item.quantite_stock) > 0
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-rose-600 dark:text-rose-400"
                            }
                          >
                            {Number(item.quantite_stock) > 0
                              ? item.quantite_stock
                              : "épuisé"}
                          </span>
                        </p>
                      )}
                    </div>

                    {/* Quantité */}
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        text="-"
                        onClick={() => handleQuantityChange(item.id, -1)}
                        className="!rounded-xl w-10"
                      />
                      <input
                        type="number"
                        className="w-20 text-center text-lg font-semibold rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                        value={item.quantity}
                        readOnly
                      />
                      <Button
                        text="+"
                        onClick={() => handleQuantityChange(item.id, 1)}
                        className="!rounded-xl w-10"
                      />
                      <span className="ml-3 text-sm text-slate-700 dark:text-slate-300">
                        {Number(item.prix).toFixed(2)} € / unité
                      </span>
                    </div>

                    {/* Message */}
                    <input
                      type="text"
                      placeholder="Ajoutez un message pour le club…"
                      className="mt-2 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm"
                      value={item.message || ""}
                      onChange={(e) => handleMessageChange(e, index)}
                    />

                    {/* Total ligne */}
                    <p className="mt-2 text-sm">
                      Total :{" "}
                      <span className="font-semibold">
                        {(Number(item.prix) * Number(item.quantity)).toFixed(2)}{" "}
                        €
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {!!cart.length && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Total général
              </p>
              <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {totalPrice.toFixed(2)} €
              </p>
            </div>
          )}
        </div>

        {/* Footer sticky */}
        <div className="sticky bottom-0 z-10 px-3 sm:px-6 py-3 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-t border-slate-200/70 dark:border-slate-800/70 flex items-center justify-end gap-2 pb-[env(safe-area-inset-bottom)]">
          <Button text="Fermer" onClick={closeModal} className="!rounded-xl" />
          <Button
            text={ordering ? "Commande…" : "Passer la commande"}
            onClick={handleOrder}
            className="!rounded-xl gap-2"
            icon={FaCartPlus}
            disabled={ordering || !cart.length}
          />
        </div>
      </div>
    </div>
  );
}
